import type { JSONContent } from '@tiptap/react';

import type { IMailCondition, IRichPart } from '../../api/mail-template-contracts';
import { nameWords, placeholderLabel, PLACEHOLDER_NODE, tokenOf } from './placeholder-text';

/**
 * A rich part's document without its editor: what the page compares, diffs and labels. Type-only
 * TipTap imports — the page reads this without loading the editor's chunk.
 */

/** A block's condition, Maily's attribute: the block is sent only when the condition holds. */
export const SHOW_IF = 'showIfKey';

/** A built-in template's locked block: `systemBlock {id}`, filled by the portal. */
export const SYSTEM_BLOCK = 'systemBlock';

/** The blocks of a body besides text (Maily's names): a heading, small print (`footer`), a button, an
 *  image, a spacer, a section, two or three columns, and a repeat over a list. */
export const DOCUMENT_BLOCKS = ['heading', 'footer', 'button', 'image', 'spacer', 'section', 'columns', 'repeat'] as const;

/** The blocks a condition can be put on — the document's top level. */
export const CONDITIONAL_BLOCKS = ['paragraph', 'bulletList', 'orderedList', 'horizontalRule', ...DOCUMENT_BLOCKS] as const;

/** A link's or a button's address that is a URL placeholder: `{{survey_url}}`. */
export const URL_TAG = /^\{\{\s*([A-Za-z_]\w*)\s*\}\}$/;

export function emptyRich(): IRichPart {
  return { doc: { type: 'doc', content: [{ type: 'paragraph' }] }, html: '' };
}

/** The document without the attributes left at their defaults (null), as it is stored. */
export function cleanDoc(node: JSONContent): JSONContent {
  const out: JSONContent = { type: node.type };
  const attrs = Object.entries(node.attrs ?? {}).filter(([, value]) => value !== null && value !== undefined);
  if (attrs.length > 0) out.attrs = Object.fromEntries(attrs);
  if (node.marks?.length) out.marks = node.marks.map((mark) => cleanDoc(mark as JSONContent) as typeof mark);
  if (node.text !== undefined) out.text = node.text;
  if (node.content?.length) out.content = node.content.map(cleanDoc);
  return out;
}

/** One string per document, whatever order its keys came in: two documents alike say the same. */
export function docKey(doc: JSONContent): string {
  const sorted = (value: unknown): unknown => {
    if (Array.isArray(value)) return value.map(sorted);
    if (value && typeof value === 'object') {
      return Object.fromEntries(
        Object.keys(value)
          .sort()
          .map((key) => [key, sorted((value as Record<string, unknown>)[key])])
      );
    }
    return value;
  };
  return JSON.stringify(sorted(cleanDoc(doc)));
}

/** "is_cc" → "Is CC": a condition's words. */
export function conditionLabel(name: string): string {
  return nameWords(name);
}

export function findCondition(conditions: readonly IMailCondition[], name: string): Nullable<IMailCondition> {
  return conditions.find((condition) => condition.name === name) ?? null;
}

/**
 * The document as text, for the history's word diff: a blank line between blocks, list items as
 * "- " or "1. ", a placeholder as its token (drawn as a pill), a rule as "———", a portal block as
 * its label — "[Submission summary]" — and a block's condition said before it — "[Only if Is CC]".
 */
export function richText(doc: JSONContent, blockLabel: (id: string) => string = (id) => id): string {
  const inline = (node: JSONContent): string =>
    (node.content ?? [])
      .map((child) => {
        if (child.type === 'text') return child.text ?? '';
        if (child.type === 'hardBreak') return '\n';
        if (child.type === PLACEHOLDER_NODE) {
          return tokenOf(String(child.attrs?.id ?? ''), (child.attrs?.fallback as Nullable<string>) ?? null);
        }
        return inline(child);
      })
      .join('');
  const block = (node: JSONContent): string => {
    const condition = node.attrs?.[SHOW_IF] as Nullable<string> | undefined;
    const when = condition ? `[Only if ${conditionLabel(condition)}] ` : '';
    const attrs = node.attrs ?? {};
    const inside = () => (node.content ?? []).map(block).join('\n\n');
    if (node.type === 'horizontalRule') return `${when}———`;
    if (node.type === SYSTEM_BLOCK) return `${when}[${blockLabel(String(attrs.id ?? ''))}]`;
    if (node.type === 'heading') return `${when}${'#'.repeat(Number(attrs.level) || 1)} ${inline(node)}`;
    if (node.type === 'button') {
      const url = attrs.isUrlVariable ? `{{${String(attrs.url ?? '')}}}` : String(attrs.url ?? '');
      return `${when}[Button: ${String(attrs.text ?? '')} → ${url}]`;
    }
    if (node.type === 'image') return `${when}[Image: ${String(attrs.alt || attrs.src || '')}]`;
    if (node.type === 'spacer') return `${when}[Space ${String(attrs.height ?? '')}px]`;
    if (node.type === 'repeat') return `${when}[For each ${placeholderLabel(String(attrs.each ?? ''))}]\n${inside()}\n[End]`;
    if (node.type === 'columns') return `${when}${(node.content ?? []).map((column) => (column.content ?? []).map(block).join('\n')).join('\n | \n')}`;
    if (node.type === 'section' || node.type === 'column') return when + inside();
    if (node.type === 'bulletList' || node.type === 'orderedList') {
      const items = (node.content ?? []).map((item, index) => {
        const mark = node.type === 'bulletList' ? '- ' : `${index + 1}. `;
        return mark + (item.content ?? []).map(block).join('\n');
      });
      return when + items.join('\n');
    }
    return when + inline(node);
  };
  return (doc.content ?? []).map(block).join('\n\n');
}
